/**
 * HeroSection — 랜딩페이지 히어로 (Figma 2828:1754)
 *
 * 배경(2828:1755)이 892px 프레임보다 큰 1037px라 overflow-hidden으로 잘라낸다.
 * 헤더(LandingHeader)는 별도 컴포넌트라 여기서 음수 마진으로 배경을 헤더 뒤까지 올린다.
 * 통계 4칸은 기존 landing-stats API 4필드와 1:1 대응한다.
 *
 * 색은 landing.css의 --lp-* 변수만 쓴다 (D-07 §1.2 랜딩페이지 예외).
 */
import { useQuery } from '@tanstack/react-query';
import { fetchLandingStats } from '../../api/landing-stats';

/** 헤더 높이(--lp-header-h)만큼 배경을 위로 끌어올려 시안의 겹침을 재현 */
const HEADER_OFFSET = 'mt-[calc(var(--lp-header-h)_*_-1)] pt-[var(--lp-header-h)]';

function formatCount(n: number | null | undefined): string {
  return n != null ? n.toLocaleString() : '-';
}

interface Props {
  onStart: () => void;
  onBrowseAgents: () => void;
}

export function HeroSection({ onStart, onBrowseAgents }: Props) {
  const { data: stats } = useQuery({
    queryKey: ['landing-stats'],
    queryFn: fetchLandingStats,
  });

  const STATS = [
    { label: '에이전트 다운로드', value: stats?.agentDownloadCount },
    { label: '사용 중인 사장님', value: stats?.activeMemberCount },
    { label: '현장 등록 수', value: stats?.siteRegisteredCount },
    { label: '진행 현장 수', value: stats?.siteInProgressCount },
  ];

  return (
    <section
      className={`lp-section-full relative isolate flex flex-col overflow-hidden bg-white ${HEADER_OFFSET}`}
    >
      {/* ── 배경 (2828:1755 / 2866:2496 "bgImg 2") ─────────────────────────
          export는 원본이 아니라 Hero 프레임에 잘린 렌더로 나온다 — 클리핑이 이미 반영돼 있어
          1920 캔버스의 x=512 지점에 놓기만 하면 된다. 어떤 뷰포트에서도 같은 상대 위치가
          되도록 1920 기준 래퍼를 중앙정렬한다.
          에셋은 고해상도 소스(3795×2118)를 2x(2816×1800)로 뽑아 레티나에서도 선명하다.
          섹션 높이가 100vh라 이미지보다 커질 수 있어 세로는 object-cover로 채운다. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute left-1/2 top-0 h-full w-[1920px] -translate-x-1/2">
          {/* fetchpriority — React 18은 camelCase `fetchPriority`를 인식하지 못해 DOM 경고를 낸다
              (React 19부터 지원). LCP 힌트는 유지하되 소문자 표준 속성명으로 전달한다. */}
          <img
            src="/landing/hero-bg.jpg"
            alt=""
            {...{ fetchpriority: 'high' }}
            className="absolute left-[512px] top-0 h-full w-[1408px] max-w-none object-cover object-top"
          />
        </div>
      </div>

      {/* ── 본문 (2828:1762) ───────────────────────────────────────────── */}
      {/* 시안 갱신: py-40 제거 + flex-1 + justify-center — 남는 높이를 채우고 세로 중앙정렬 */}
      <div className="mx-auto flex w-full max-w-[1632px] flex-1 flex-col justify-center gap-20 px-8 lg:px-20">

        {/* 헤드라인 블록 (2828:1763) */}
        <div className="flex flex-col items-start justify-center gap-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-[var(--lp-brand-border)] bg-[var(--lp-surface-glass)] px-[17px] py-[9px] shadow-[var(--lp-shadow-pill)]">
            <span className="size-2 rounded-full bg-[var(--lp-brand)]" />
            <span className="text-sm font-bold leading-none text-[var(--lp-brand)]">베타 서비스 오픈</span>
          </span>

          <h1 className="text-[40px] font-bold leading-[1.3] text-[var(--lp-text)] sm:text-[56px] lg:text-[64px]">
            인테리어, 이제는
            <br />
            <span className="text-[var(--lp-brand)]">다르게</span> 해볼까요!
          </h1>

          <p className="text-base leading-[1.3] text-[var(--lp-text)] lg:text-lg">
            고객 만나고, 현장 뛰고, 견적 쓰고, 전화하고, 엑셀로 정리하고 하루가 너무나 짧습니다.
            <br />
            나만의 스튜디오에서 에이전트로, 일하는 방식을 바꿔보세요.
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <button type="button" onClick={onStart} className="lp-btn-brand">
              스튜디오 입장하기
              <img src="/landing/ic-arrow.svg" alt="" className="size-5" />
            </button>
            <button type="button" onClick={onBrowseAgents} className="lp-btn-ghost">
              에이전트 둘러보기
            </button>
          </div>
        </div>

        {/* 통계 + 스토어 (2828:1777) */}
        <div className="flex w-full max-w-[976px] flex-col items-start justify-center gap-4">
          <div className="w-full max-w-[768px] rounded-[24px] border border-[var(--lp-border)] bg-white/10 p-[9px]">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {STATS.map(({ label, value }) => (
                <div key={label} className="flex flex-col items-center justify-center py-4">
                  <span className="text-[40px] font-bold leading-[48px] text-[var(--lp-brand-deep)]">
                    {formatCount(value)}
                  </span>
                  <span className="pt-1.5 text-center text-base font-bold leading-none text-[var(--lp-brand-deep)]">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-start justify-center gap-4">
            <a href="#" className="lp-store-link">
              <img src="/landing/ic-googleplay.png" alt="" className="size-6" />
              Google Play
            </a>
            <a href="#" className="lp-store-link">
              <img src="/landing/ic-appstore.svg" alt="" className="size-6" />
              App Store
            </a>
          </div>

          <p className="text-xs font-medium leading-none text-[var(--lp-text)]">
            — 사무실은 웹으로, 현장은 앱으로 함께 쓰세요 —
          </p>
        </div>
      </div>
    </section>
  );
}
